import { Link } from '@tanstack/react-router';
import { Kanban, Lock } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useBoards } from '@/hooks/use-boards';
import { httpStatus } from '@/lib/http-status';
import { useExtensionsStore } from '@/stores/extensions';
import { STATUS_STYLE } from '@/features/tasks/components/overview/board-status';
import { sortProjectBoards } from '../lib/project-boards';

/**
 * Every board linked to the project that the reader may open (HW-983) — the
 * `/boards` list is already narrowed to those — by name. A board is linked
 * from its Board settings → General → Project, or when it is created.
 */
export function BoardsTab({ projectId }: { projectId: string }) {
  const isTaskManagerEnabled = useExtensionsStore(
    (s) => s.isExtensionEnabled('task-manager') || !s.isLoaded || s.loadFailed,
  );
  const { data, isLoading, isError, error } = useBoards({ projectId });
  const boards = sortProjectBoards(data);

  if (!isTaskManagerEnabled) {
    return <p className="text-sm text-muted-foreground">Boards are switched off for the company.</p>;
  }
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-lg" />
        ))}
      </div>
    );
  }
  // Refused the list altogether (no Open Boards): there is no board to show, which is an answer.
  if (isError && httpStatus(error) !== 403) {
    return <p className="text-sm text-destructive">The project's boards could not be loaded.</p>;
  }
  if (boards.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No board you can open is linked to this project. A board is linked from its Board settings → General →
        Project.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border rounded-lg border border-border">
      {boards.map((board) => (
        <li key={board.id}>
          <Link
            to="/boards"
            search={{ board: board.id }}
            className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
          >
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white"
              style={{ background: board.color }}
            >
              <Kanban className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-foreground">{board.name}</span>
                <span className="font-mono text-[11px] text-muted-foreground">{board.slug}</span>
              </span>
              {board.summary && <span className="block truncate text-xs text-muted-foreground">{board.summary}</span>}
            </span>
            {board.visibility === 'members_only' && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="h-3 w-3" /> Private
              </span>
            )}
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground w-24 justify-end">
              <span className={`h-2 w-2 rounded-full ${STATUS_STYLE[board.status].dot}`} />
              {STATUS_STYLE[board.status].label}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
